import { APIError, fetcher } from "@certn/api";
import type { components } from "@certn/api/generated-schemas/applicant-portal";
import {
  showToast,
  ButtonGroup,
  GhostButton,
  PrimaryButton,
  Stack,
  Text,
} from "@certn/cascadia";
import {
  BasicButton,
  FileUpload,
  Loader,
  SectionMessage,
  Space,
  Signature,
} from "@certn/cascadia-react";
import { CertnDocumentScroll } from "@certn/cascadia-react/miscellaneous/contract/CertnDocumentScroll";
import {
  CascadiaControlProps,
  CascadiaJsonFormContext,
  withCascadiaControlProps,
} from "@certn/forms";
import { DEFAULT_FILE_TYPES } from "@certn/utils";
import { css } from "@emotion/css";
import * as Sentry from "@sentry/react";
import {
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import { useTranslation, Trans } from "react-i18next";
import { useParams } from "react-router-dom";
import useSWR from "swr";

import { ConsentRequiredModal } from "./ConsentRequiredModal";
import { useApplication } from "../../ApplicationContext";
import { AuDlConsentLayoutContext } from "../../layouts/AuDlConsentLayoutContext";

// API response and request types are sourced from the backend OpenAPI
// schema (regenerated via `packages/api/update-types.sh`). Aliasing them
// here keeps the call sites readable without re-declaring the shapes.
type AuDlConsentDownloadResponse =
  components["schemas"]["AuDlConsentDownloadResponse"];
type AuDlConsentStatusResponse =
  components["schemas"]["AuDlConsentStatusResponse"];
type AuDlConsentSignRequest = components["schemas"]["AuDlConsentSignRequest"];
type AuDlConsentSignResponse = components["schemas"]["AuDlConsentSignResponse"];
type AuDlConsentCompleteResponse =
  components["schemas"]["AuDlConsentCompleteResponse"];

interface FileInfo {
  key?: string;
  name: string;
  size?: number;
  type?: string;
  url?: string;
}

// Consent flow states. Mirrors the AFP flow but third-party states
// represent state-agency forms that have no agent-consent provision (NT
// today) - the applicant downloads the presigned bundle, gets a witness
// wet-ink signature, and re-uploads it. Some third-party states still
// also capture an in-app signature first; NT skips that step entirely
// (driven by ``requires_in_app_signature`` on the status response).
enum ConsentFlowState {
  INITIAL = "INITIAL",
  SIGNING = "SIGNING",
  PENDING_THIRD_PARTY_SIGNATURE = "PENDING_THIRD_PARTY_SIGNATURE",
  COMPLETING = "COMPLETING",
  COMPLETE = "COMPLETE",
}

// Upload type for AU DL consent re-upload (witness-signed PDF).
enum AuDlUploadType {
  CONSENT_DOCUMENT = "AU_DL_CONSENT_DOCUMENT",
}

export function AuDlConsentControl(props: CascadiaControlProps) {
  const { handleChange, path } = props;
  const { t } = useTranslation();
  const { caseId } = useParams();
  const { isSubmitted, submit } = useContext(CascadiaJsonFormContext);
  const { subLabel } = useContext(AuDlConsentLayoutContext);
  const { navigateToSection, prevSection, partiallySaveData } =
    useApplication();

  const [flowState, setFlowState] = useState<ConsentFlowState>(
    ConsentFlowState.INITIAL
  );
  const [signatureData, setSignatureData] = useState<string>("");
  const [showConsentRequiredModal, setShowConsentRequiredModal] =
    useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<FileInfo[]>([]);
  const [signedFormUrl, setSignedFormUrl] = useState<string | undefined>();
  const [documentReviewed, setDocumentReviewed] = useState(false);

  // Refs update synchronously and prevent useEffect from resetting state
  // while a submission is mid-flight.
  const isSubmittingRef = useRef(false);

  const hasSignature = !!signatureData;

  const PrivacyMessage = useMemo(
    () => (
      <SectionMessage
        icon="verified_user"
        sectionTitle={t("Your privacy is important to us")}
        type="success"
      >
        {t(
          "Certn will only collect information that is needed to complete your check."
        )}
      </SectionMessage>
    ),
    [t]
  );

  // Pre-signed URL for the per-state consent bundle (filled but unsigned).
  const { data: downloadData, isLoading: isPdfLoading } =
    useSWR<AuDlConsentDownloadResponse>(
      caseId
        ? `/api/internal/applicant/case/${caseId}/au-dl-consent/download/`
        : null,
      fetcher.request
    );

  const {
    data: statusData,
    isLoading: isStatusLoading,
    mutate: refetchStatus,
  } = useSWR<AuDlConsentStatusResponse>(
    caseId
      ? `/api/internal/applicant/case/${caseId}/au-dl-consent/status/`
      : null,
    fetcher.request
  );

  const presignedUrl = downloadData?.presigned_url || "";

  const isTransitioning =
    flowState === ConsentFlowState.SIGNING ||
    flowState === ConsentFlowState.COMPLETING;

  // ``requires_in_app_signature === false`` (NT today) means there is no
  // signature pad — the applicant downloads the presigned form, signs it
  // physically with an Authorised Officer witness, and uploads it back.
  // Defaults to true so the existing in-app flow stays the fallback for
  // any state where the backend hasn't returned the field yet.
  const requiresInAppSignature = statusData?.requires_in_app_signature ?? true;

  const isNextDisabled = useMemo(
    () =>
      isTransitioning ||
      (flowState === ConsentFlowState.INITIAL &&
        requiresInAppSignature &&
        !hasSignature) ||
      (flowState === ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE &&
        uploadedFiles.length === 0),
    [
      isTransitioning,
      flowState,
      hasSignature,
      uploadedFiles.length,
      requiresInAppSignature,
    ]
  );

  const nextButtonText = useMemo(() => {
    if (flowState === ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE) {
      return t("Complete Consent");
    }
    if (flowState === ConsentFlowState.INITIAL && hasSignature) {
      return t("Sign");
    }
    return t("Next step");
  }, [flowState, hasSignature, t]);

  const documentReviewLabel = t(
    "I have reviewed the above information and confirm it is correct."
  );

  const updateConsentState = useCallback(
    (state: ConsentFlowState, reviewed: boolean, signedUrl?: string) => {
      setFlowState(state);
      setDocumentReviewed(reviewed);
      setSignedFormUrl(signedUrl);
      if (!reviewed) setSignatureData("");
    },
    []
  );

  // Initialise state from backend status. Three buckets:
  //   - complete (signed + non-third-party state, OR third-party uploaded)
  //   - signed but waiting for the witness re-upload (third-party state)
  //   - initial (signature pad)
  useEffect(() => {
    if (!statusData) return;
    if (isSubmittingRef.current) return;

    const {
      is_complete,
      is_signed,
      is_third_party_required,
      requires_in_app_signature,
      signed_form_download_url,
    } = statusData;
    const inAppSig = requires_in_app_signature ?? true;
    const isComplete = is_complete || (is_signed && !is_third_party_required);

    if (isComplete) {
      updateConsentState(ConsentFlowState.COMPLETE, true);
      handleChange(path, true);
    } else if (is_third_party_required && !inAppSig) {
      // No in-app signature step (NT). Land directly in the upload
      // screen and feed the *presigned* bundle URL as the download
      // target — the applicant signs it on paper with an Authorised
      // Officer witness before re-uploading.
      updateConsentState(
        ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE,
        true,
        presignedUrl || undefined
      );
      handleChange(path, false);
    } else if (is_signed && is_third_party_required) {
      updateConsentState(
        ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE,
        true,
        signed_form_download_url ?? undefined
      );
    } else {
      updateConsentState(ConsentFlowState.INITIAL, false);
      handleChange(path, false);
    }
  }, [statusData, handleChange, path, updateConsentState, presignedUrl]);

  useEffect(() => {
    if (
      isSubmitted &&
      flowState === ConsentFlowState.INITIAL &&
      requiresInAppSignature &&
      !hasSignature
    ) {
      setShowConsentRequiredModal(true);
    }
  }, [isSubmitted, flowState, hasSignature, requiresInAppSignature]);

  const handleSignatureChange = useCallback((signature: string) => {
    setSignatureData(signature);
    setDocumentReviewed(true);
  }, []);

  const handleDocumentReviewChange = useCallback((isSelected: boolean) => {
    setDocumentReviewed(isSelected);
  }, []);

  const handleSignatureClear = useCallback(() => {
    setSignatureData("");
    setDocumentReviewed(false);
  }, []);

  const handleSign = async () => {
    if (!caseId || !signatureData) return;

    isSubmittingRef.current = true;
    setFlowState(ConsentFlowState.SIGNING);
    handleChange(path, true);

    try {
      const requestBody: AuDlConsentSignRequest = {
        applicant_signature: signatureData,
      };

      const response = await fetcher.submit<AuDlConsentSignResponse>(
        `/api/internal/applicant/case/${caseId}/au-dl-consent/create-claim/`,
        requestBody
      );

      if (response.is_third_party_required) {
        // Third-party state (NT today) - applicant signed in-app but the
        // bundle still needs a witness wet-ink signature before completion.
        // The signed form URL is required to render the download link; if the
        // backend omits it, revert to INITIAL and surface the error rather
        // than dropping the user into a broken upload state.
        if (!response.signed_form_download_url) {
          isSubmittingRef.current = false;
          handleChange(path, false);
          setFlowState(ConsentFlowState.INITIAL);
          showToast(
            t(
              "We couldn't prepare the consent form for download. Please try again."
            ),
            { type: "error" }
          );
          Sentry.captureException(
            new Error(
              "AU DL consent third-party signing required without signed_form_download_url"
            )
          );
          return;
        }

        handleChange(path, false);
        setSignedFormUrl(response.signed_form_download_url);
        setFlowState(ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE);
        isSubmittingRef.current = false;
        await refetchStatus();
      } else {
        // Non-third-party state - consent complete, ready for next section.
        setFlowState(ConsentFlowState.COMPLETE);
        // Keep isSubmittingRef true until the user navigates away.
      }
    } catch (e) {
      console.error("Error signing AU DL consent", e);
      isSubmittingRef.current = false;
      handleChange(path, false);
      setFlowState(ConsentFlowState.INITIAL);
      showToast(
        t("Something went wrong signing the consent form. Please try again."),
        {
          type: "error",
        }
      );
      Sentry.captureException(e);
    }
  };

  const handleComplete = async () => {
    if (!caseId) return;

    if (uploadedFiles.length === 0) {
      showToast(t("Please upload the witness-signed consent form first."), {
        type: "error",
      });
      return;
    }

    isSubmittingRef.current = true;
    setFlowState(ConsentFlowState.COMPLETING);

    try {
      const response = await fetcher.submit<AuDlConsentCompleteResponse>(
        `/api/internal/applicant/case/${caseId}/au-dl-consent/complete/`,
        {}
      );

      if (response.is_complete) {
        handleChange(path, true);
        setFlowState(ConsentFlowState.COMPLETE);
      } else {
        // Backend reports incomplete - keep applicant in the upload state.
        isSubmittingRef.current = false;
        showToast(t("Please upload the witness-signed consent form first."), {
          type: "error",
        });
        setFlowState(ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE);
      }
    } catch (e) {
      console.error("Error completing AU DL consent", e);
      isSubmittingRef.current = false;

      if (e instanceof APIError && e.status === 400) {
        showToast(t("Please upload the witness-signed consent form first."), {
          type: "error",
        });
      } else {
        showToast(t("Something went wrong. Please try again."), {
          type: "error",
        });
        Sentry.captureException(e);
      }
      setFlowState(ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE);
    }
  };

  const handleBack = async () => {
    if (prevSection) {
      try {
        await partiallySaveData();
      } catch (e) {
        if (e instanceof APIError && e.status === 400) {
          showToast(t("Error saving your progress"), { type: "error" });
        } else {
          showToast(t("Something went wrong, try again"), { type: "error" });
          Sentry.captureException(e);
        }
      }
      navigateToSection(prevSection);
    }
  };

  const handleNextStep = () => {
    if (flowState === ConsentFlowState.INITIAL) {
      // Mirror isNextDisabled: the "signature required" modal is only
      // meaningful for in-app-signature flows. NT (no in-app signature)
      // shouldn't reach here in practice — the status useEffect lands it
      // straight in PENDING_THIRD_PARTY_SIGNATURE — but guard defensively
      // so the button-enabled state and click behaviour stay consistent.
      if (requiresInAppSignature && !hasSignature) {
        setShowConsentRequiredModal(true);
        return;
      }
      handleSign();
    } else if (flowState === ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE) {
      handleComplete();
    } else if (flowState === ConsentFlowState.COMPLETE) {
      submit();
    }
  };

  // INITIAL is reachable for in-app-signature states only — NT lands
  // straight in PENDING_THIRD_PARTY_SIGNATURE via the status effect above.
  // No dedicated branch needed in renderContent; the existing INITIAL
  // branch already renders the signature pad and the
  // PENDING_THIRD_PARTY_SIGNATURE branch already renders the
  // download + upload UI.

  if (isPdfLoading || isStatusLoading) {
    return <Loader height="500px" />;
  }

  const renderContent = () => {
    if (flowState === ConsentFlowState.COMPLETE) {
      return (
        <SectionMessage
          icon="check_circle"
          type="success"
          sectionTitle={t("Consent Complete")}
        >
          {t(
            "Your Australian Driver's Licence consent has been successfully recorded."
          )}
        </SectionMessage>
      );
    }

    if (
      flowState === ConsentFlowState.PENDING_THIRD_PARTY_SIGNATURE ||
      flowState === ConsentFlowState.COMPLETING
    ) {
      return (
        <Stack gap="space-4">
          {PrivacyMessage}

          <CertnDocumentScroll
            presignedUrl={signedFormUrl || ""}
            isSigned={true}
            skipHasToScroll={true}
            label={documentReviewLabel}
          />

          <SectionMessage
            icon="info"
            type="info"
            sectionTitle={t("Authorised Officer Signature Required")}
          >
            <Stack
              gap="space-2"
              data-testid="au-dl-consent-third-party-instructions"
            >
              <span>
                {t(
                  "Please download and sign the Traffic History Application form and get your photo ID certified/endorsed by an Authorised Officer."
                )}
              </span>
              <span>
                {t(
                  '"Certified/endorsed" means the Authorised Officer checks your original photo ID, confirms it is genuine and that the photo is really you, then adds a statement, signature, and stamp to the copy. The Authorised Officer also needs to sign the Traffic History Application.'
                )}
              </span>
              <span>
                <Trans
                  i18nKey="au-dl-consent-nt-who-can-certify"
                  defaults="Learn more about <0>who can certify your document</0>."
                  components={[
                    <a
                      key="link"
                      href="https://www.ag.gov.au/legal-system/statutory-declarations/about-commonwealth-statutory-declarations/who-can-witness-commonwealth-statutory-declaration"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      who can certify your document
                    </a>,
                  ]}
                />
              </span>
              <span>
                {t(
                  "Once signed and certified, scan the signed form and the certified copy of your photo ID into a single document, then log back in and upload it below:"
                )}
              </span>
            </Stack>
          </SectionMessage>

          <FileUpload
            files={uploadedFiles}
            setFiles={setUploadedFiles}
            allowedFileTypes={DEFAULT_FILE_TYPES}
            requestUrl="/api/internal/applicant/generate-presigned-url/"
            bucketDirectory="applicant_uploads"
            caseId={caseId}
            uploadType={AuDlUploadType.CONSENT_DOCUMENT}
            testID="au-dl-consent-third-party-upload"
            maxSizeBytes={10 * 1024 * 1024}
            triggerButton={
              <BasicButton leftIcon="add">{t("Upload document")}</BasicButton>
            }
          />
        </Stack>
      );
    }

    return (
      <Stack gap="space-4">
        {PrivacyMessage}

        <CertnDocumentScroll
          presignedUrl={presignedUrl}
          isSigned={documentReviewed || hasSignature}
          onChange={handleDocumentReviewChange}
          skipHasToScroll={true}
          label={documentReviewLabel}
        />

        <Signature
          onChange={handleSignatureChange}
          onClear={handleSignatureClear}
          disabled={isTransitioning}
          width={580}
          height={200}
          className={styles.signature}
        />
      </Stack>
    );
  };

  return (
    <Stack gap="space-4">
      <div className={styles.heading}>
        <Text as="h1" style="form/page-title" testID="au-dl-consent-title">
          {t("Australian Driver's Licence Consent")}
        </Text>

        {subLabel && (
          <Text
            style="form/body-primary"
            color="text-weak"
            testID="au-dl-consent-sub-title"
          >
            {subLabel}
          </Text>
        )}
      </div>

      {renderContent()}

      <div className={styles.actionBar}>
        <ButtonGroup>
          {prevSection && (
            <GhostButton
              leftIcon="chevron_left"
              onPress={handleBack}
              testID="section-back-button"
            >
              {t("Back")}
            </GhostButton>
          )}
          <Space />

          <PrimaryButton
            onPress={handleNextStep}
            variant="brand-primary"
            isLoading={isTransitioning}
            isDisabled={isNextDisabled}
            testID="section-next-step-button"
          >
            {nextButtonText}
          </PrimaryButton>
        </ButtonGroup>
      </div>

      <ConsentRequiredModal
        show={showConsentRequiredModal}
        onClose={() => setShowConsentRequiredModal(false)}
        alternativeDialogContent={{
          header: t("Signature required"),
          content: (
            <Trans>
              Please review the consent form and provide your signature using
              the signature pad before continuing.
            </Trans>
          ),
        }}
      />
    </Stack>
  );
}

const styles = {
  heading: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  }),
  actionBar: css({
    borderTop: "1px solid var(--border-base)",
    paddingTop: "calc(var(--space-4) - 1px)",
    paddingBottom: "var(--space-4)",
    marginTop: "var(--space-5)",
  }),
  signature: css({
    width: "100%",
    maxWidth: "100%",
  }),
};

AuDlConsentControl.displayName = "AuDlConsentControl";

export const AuDlConsentControlRenderer =
  withCascadiaControlProps(AuDlConsentControl);
